/**
 * SetupFunnelService — makes setup abandonment measurable.
 *
 * `/genie-hub/unfinished-setups` exists because setups get abandoned often
 * enough to need a recovery page. Nobody was counting. These are the events
 * that turn "we think people drop off somewhere" into a number.
 *
 * Same transport as ActivationFunnelService: the `logger` edge function, which
 * is the only path that works for ordinary (non-admin) customers. Fire and
 * forget, never blocks, never surfaces an error. See
 * docs/handoff/time-to-value-instrumentation.md for the contract.
 */
import { LoggerService } from "@/services/logger/LoggerService";
import { UserService } from "@/services/users/UserService";
import type { SetupFunnelStep } from "@/types/analytics/setup-funnel";

const emit = async (
  step: SetupFunnelStep,
  conversationId: string | null,
  detail: Record<string, string | number | boolean | null> = {},
): Promise<void> => {
  try {
    const {
      data: { user },
    } = await UserService.getUser();
    if (!user?.id) return;

    await LoggerService.logFunnelStep({
      userId: user.id,
      step,
      genieId: null,
      channel: null,
      method: null,
      detail: { ...detail, conversation_id: conversationId },
      occurredAt: new Date().toISOString(),
    });
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn("[setup-funnel] could not record step", step, error);
    }
  }
};

export class SetupFunnelService {
  /** A setup conversation began. */
  static started(conversationId: string | null, mode: string | null): void {
    void emit("setup_started", conversationId, { mode });
  }

  /**
   * The setup moved to a phase. THIS is the drop-off measurement: the last
   * phase a customer reached before they stopped is where they dropped.
   * Phases come from `SetupPhase` in useGenieSetupReducer.
   */
  static phaseReached(conversationId: string | null, phase: string): void {
    void emit("setup_phase_reached", conversationId, { phase });
  }

  /** A phase failed. `phase` is where, `reason` is a short classifier. */
  static phaseFailed(conversationId: string | null, phase: string, reason: string): void {
    void emit("setup_phase_failed", conversationId, { phase, reason: reason.slice(0, 120) });
  }

  /** Derived setup state was written down, so recovery no longer needs the transcript. */
  static stateCaptured(conversationId: string | null, kind: string): void {
    void emit("setup_state_captured", conversationId, { kind });
  }

  /** Someone tried to resume. `source` tells us whether the fix is working. */
  static resumeAttempted(conversationId: string | null, source: string, ok: boolean): void {
    void emit("setup_resume_attempted", conversationId, { source, ok });
  }

  /** The customer dismissed an unfinished setup rather than resuming it. */
  static abandoned(conversationId: string | null, how: string): void {
    void emit("setup_abandoned", conversationId, { how });
  }

  /** A Genie was created out of this setup. The setup funnel's success state. */
  static completed(conversationId: string | null): void {
    void emit("setup_completed", conversationId, {});
  }
}
